import React, { useEffect, useState } from "react";
import { useScrollSpy } from "./hooks/useScrollSpy";
import './scss/_mainmenu.css';

const LINKS = [
    { href: "#behandlinger", label: "Behandlinger", id: "behandlinger" , children: []},
    { href: "#priser",       label: "Priser",       id: "priser", children: [] },
    { href: "#omoss",        label: "Om Meg",       id: "omoss", children: [] },
    { href: "#kontakt",      label: "Kontakt",      id: "kontakt", children: [] },
] as Array<{
    id: string;
    label: string;
    href?: string;
    children?: Array<{ id: string; label: string; href?: string }>;
}>
;

export default function MainMenu() {
    const [activeHash, setActiveHash] = useState<string>(window.location.hash ?? "");
    // active section with IntersectionObserver
    const activeFromSpy = useScrollSpy(LINKS.map((l) => l.id),{
        headerOffset: 300,
        threshold: 0.5,    // 50% секції у видимій області
    });

    useEffect(() => {
        const onHashChange = () => setActiveHash(window.location.hash ?? "");
        window.addEventListener("hashchange", onHashChange, { passive: true } as any);
        return () => window.removeEventListener("hashchange", onHashChange);
    }, []);

    const effectiveActive = activeFromSpy ? `#${activeFromSpy}` : activeHash;

    // 1) Гамбургер / відкриття мобільного меню
    const [isOpen, setIsOpen] = useState<boolean>(false);
    // 2) aria-expanded для #cs-expanded
    const ariaExpanded = isOpen ? "true" : "false";
    // 3) Дропдауни (".cs-dropdown")
    const [openDropdowns, setOpenDropdowns] = useState<Set<string>>(() => new Set());

    // Еквівалент:
    // CShamburgerMenu.addEventListener('click', ...) + classList.toggle('cs-active') + CSbody.classList.toggle('cs-open')
    const toggleHamburger = () => setIsOpen((o) => !o);

    // Еквівалент:
    // function ariaExpanded() { ...toggle true/false... }
    // (У нас керується через `ariaExpanded` вище)

    // Еквівалент mobile nav toggle (для кожного .cs-dropdown)
    const toggleDropdown = (id: string) => {
        setOpenDropdowns((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const isDropdownOpen = (id: string) => openDropdowns.has(id);

    // Блокування скролу body (аналог .cs-open на body)
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add("cs-open");
            //document.body.style.overflow = "hidden";
        } else {
            document.body.classList.remove("cs-open");
            document.body.style.overflow = "";
        }
        return () => {
            document.body.classList.remove("cs-open");
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    // Клік по пункту-якорю: плавний скрол + закрити моб. меню
    const handleAnchorClick = (e: React.MouseEvent, id: string) => {
        e.preventDefault();
        const el = document.getElementById(id);
        if (!el) return;

        const headerOffset = 300; // синхронно з useScrollSpy
        const rect = el.getBoundingClientRect();
        const absoluteY = window.pageYOffset + rect.top - 40;

        window.scrollTo({ top: absoluteY, behavior: "smooth" });
        setActiveHash(`#${id}`);
        setIsOpen(false);
    };

    return (
        <nav aria-label="Hovedmeny" className={isOpen ? "cs-active" : ""}>
            <button  className={`cs-toggle ${isOpen ? "cs-active" : ""}`}

                     aria-label={isOpen ? "Lukk meny" : "Åpne meny"}
                     onClick={toggleHamburger}
            >
                <div className="cs-box" aria-hidden="true">
                    <span className="cs-line cs-line1" aria-hidden="true"></span>
                    <span className="cs-line cs-line2" aria-hidden="true"></span>
                    <span className="cs-line cs-line3" aria-hidden="true"></span>
                </div>
            </button>
            <div className="cs-ul-wrapper" >
            <ul className="main-menu cl-ul" aria-expanded={ariaExpanded} role="menubar"  id="cs-expanded" aria-controls="cs-expanded">
                {LINKS.map((item) => {
                    const isActive = effectiveActive === (item.href ?? "");
                    const hasChildren = !!item.children?.length;
                    const dropdownOpen = hasChildren && isDropdownOpen(item.id);

                    return (
                        <li
                            key={item.id}
                            className={`cs-item cs-li ${hasChildren ? "cs-dropdown" : ""} ${dropdownOpen ? "cs-active" : ""}`}
                            role="none"
                        >
                            {hasChildren ? (
                                <>
                                    {/* Кнопка для відкриття підменю (замість addEventListener on .cs-dropdown) */}
                                    <button
                                        type="button"
                                        className="cs-link cs-dropdown-toggle"
                                        aria-expanded={dropdownOpen}
                                        aria-controls={`sub-${item.id}`}
                                        onClick={() => toggleDropdown(item.id)}
                                    >
                                        {item.label}
                                        <span className="cs-caret" aria-hidden="true" />
                                    </button>

                                    <ul
                                        id={`sub-${item.id}`}
                                        className="cs-submenu"
                                        role="menu"
                                        hidden={!dropdownOpen}
                                    >
                                        {item.children!.map((child) => (
                                            <li key={child.id} className="cs-subitem" role="none">
                                                <a
                                                    className="cs-sublink"
                                                    href={child.href ?? "#"}
                                                    role="menuitem"
                                                    onClick={() => setIsOpen(false)}
                                                >
                                                    {child.label}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ) : (
                                <a
                                    className={`main-menu-link ${isActive ? "is-active" : ""}`}
                                    href={item.href ?? "#"}
                                    role="menuitem"
                                    onClick={(e) =>
                                        item.href?.startsWith("#") ? handleAnchorClick(e, item.id) : undefined
                                    }
                                >
                                    {item.label}
                                </a>
                            )}
                        </li>
                    );
                })}
            </ul>
            </div>
        </nav>
    );
}
